import { Plus } from "lucide-react";

import { cn } from "@monorepo-apps/ui/src/lib/utils";

import { planBlockedReason, rejectBlockedReason } from "@/application/use-cases/b2c/backlog-triage";
import type { IterationView } from "@/application/use-cases/b2c/build-b2c-board";
import type { BacklogActions } from "@/presentation/components/b2c/backlog-actions";
import { BacklogRejectButton } from "@/presentation/components/b2c/backlog-reject-button";
import { CopyCode } from "@/presentation/components/b2c/copy-code";
import { KIND_LABEL, PRIORITY_BADGE, PRIORITY_LABEL } from "@/presentation/data/b2c-board";
import type { B2cTaskRow } from "@/infrastructure/supabase/b2c-board.types";

export function BacklogRow({
  task,
  target,
  canWrite,
  actions,
}: {
  task: B2cTaskRow;
  target: IterationView | null;
  canWrite: boolean;
  actions: BacklogActions;
}) {
  const planBlocked = planBlockedReason(task, target);

  return (
    <li
      onClick={() => actions.onOpenTask(task)}
      className="border-border/60 hover:bg-muted/50 -mx-1 flex cursor-pointer items-start gap-2 rounded-md border-t px-1 py-1.5 first:border-t-0"
    >
      <CopyCode code={task.code} className="mt-0.5 w-[82px] whitespace-nowrap" />
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          actions.onOpenTask(task);
        }}
        aria-label={`Open ${task.code}: ${task.title}`}
        className="focus-visible:ring-primary/40 min-w-0 flex-1 rounded text-left text-[12.5px] leading-snug focus-visible:ring-2 focus-visible:outline-none"
      >
        <span
          className={cn(
            "font-data mr-1.5 rounded px-1 py-px text-[9.5px] font-bold tracking-[0.1em]",
            PRIORITY_BADGE[task.priority].className
          )}
          title={PRIORITY_LABEL[task.priority]}
        >
          {PRIORITY_BADGE[task.priority].text}
        </span>
        {task.kind === "decision" || task.kind === "reconfigure" ? (
          <span className="font-data text-muted-foreground mr-1.5 text-[10px] tracking-wide uppercase">
            {KIND_LABEL[task.kind]}
          </span>
        ) : null}
        {task.title}
        {task.description ? (
          <span className="text-muted-foreground mt-0.5 line-clamp-2 block text-[11.5px] leading-snug">
            {task.description}
          </span>
        ) : null}
      </button>

      {canWrite ? (
        <div
          className="flex shrink-0 items-center gap-1.5"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {planBlocked !== null || target === null ? (
            <span className="text-muted-foreground max-w-[190px] text-[11px] italic">
              {planBlocked}
            </span>
          ) : (
            <button
              type="button"
              onClick={() => actions.onPlan(task, target.iteration.id)}
              aria-label={`Add ${task.code} to iteration ${target.iteration.number}`}
              className="border-border hover:border-primary/50 hover:text-primary inline-flex h-7 items-center gap-1 rounded-lg border px-2 text-[11px] font-bold whitespace-nowrap"
            >
              <Plus className="size-3.5" aria-hidden />
              Add to it. {target.iteration.number}
            </button>
          )}

          {rejectBlockedReason(task) !== null ? (
            <span className="w-[62px] shrink-0" aria-hidden />
          ) : (
            <BacklogRejectButton code={task.code} onReject={() => actions.onReject(task)} />
          )}
        </div>
      ) : null}
    </li>
  );
}
